import { questionCategoryEnum } from '@kiku/db/schema';
import { z } from 'zod';

export const QUESTION_CATEGORIES = questionCategoryEnum.enumValues;

/**
 * Canonical display/sort order for question categories.
 * Eligibility → Realistic Job Preview → Nice to Have → Assessment → Information.
 */
export const CATEGORY_DISPLAY_ORDER: (typeof QUESTION_CATEGORIES)[number][] = [
  'eligibility',
  'job_expectations',
  'nice_to_have',
  'assessment',
  'information'
];

export const scoringExamplesSchema = z.object({
  score1: z.string(),
  score3: z.string(),
  score5: z.string()
});

export type ScoringExamples = z.infer<typeof scoringExamplesSchema>;

export const interviewQuestionSchema = z.object({
  id: z.string().uuid(),
  jobId: z.string().uuid(),
  category: z.enum(QUESTION_CATEGORIES),
  text: z.string(),
  position: z.number().int(),
  isOptional: z.boolean(),
  competencyId: z.string().uuid().nullable(),
  competencyName: z.string().nullable(),
  scoringExamples: scoringExamplesSchema.nullable(),
  aiGenerated: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date()
});

export type InterviewQuestion = z.infer<typeof interviewQuestionSchema>;

export const upsertQuestionInputSchema = z.object({
  category: z.enum(QUESTION_CATEGORIES),
  text: z.string().min(1),
  position: z.number().int().min(0),
  isOptional: z.boolean().default(false),
  competencyName: z.string().optional(),
  scoringExamples: scoringExamplesSchema.optional()
});

export type UpsertQuestionInput = z.infer<typeof upsertQuestionInputSchema>;

export const saveQuestionsInputSchema = z.object({
  questions: z.array(upsertQuestionInputSchema)
});

export type SaveQuestionsInput = z.infer<typeof saveQuestionsInputSchema>;

export const applyQuestionsToJobsInputSchema = z.object({
  sourceQuestions: z.array(upsertQuestionInputSchema),
  jobIds: z.array(z.string().uuid()).min(1)
});

export type ApplyQuestionsToJobsInput = z.infer<typeof applyQuestionsToJobsInputSchema>;

export const competencySchema = z.object({
  id: z.string().uuid(),
  name: z.string()
});

export type Competency = z.infer<typeof competencySchema>;

export const regenerateScoringExamplesInputSchema = z.object({
  questionText: z.string().min(1)
});

export type RegenerateScoringExamplesInput = z.infer<typeof regenerateScoringExamplesInputSchema>;

export class InterviewsJobNotFoundError extends Error {
  constructor(id: string) {
    super(`Job ${id} not found`);
    this.name = 'InterviewsJobNotFoundError';
  }
}

export class InterviewsInvalidJobStatusError extends Error {
  constructor(id: string, status: string) {
    super(`Job ${id} is not in draft status (current: ${status})`);
    this.name = 'InterviewsInvalidJobStatusError';
  }
}
